'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useAuth } from '@/features/auth/hooks'
import { exchangeToTradeVenue } from '@/lib/configs/exchange-registry'
import { accountKeys } from '@/service/account.hook'
import {
  deriveDisconnect,
  fetchVenues,
  hyperliquidDisconnectAgent,
  papertradeDisconnect,
  paradigmDisconnect,
} from '@/service/venue.api'
import { Exchange, TradeVenue } from '@/types/generated/common/types'
import { ApiKeyGuide, Success, VenueInfo } from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

export const venueKeys = {
  all: ['venues'] as const,
}

/**
 * Server-side venue registry (`get_venues`). Drives which venues are offered
 * for signup (`newAccount`), whether order routing is enabled (`enabled`),
 * and whether onboarding uses the OAuth popup (`oauth` present) or manual
 * API-key entry. `options.enabled` defaults true — most callers (including
 * pre-login signup flows) need this regardless of auth state; pass `false`
 * only for a call site that has no use for venue data while logged out (see
 * `ActiveExchangeSync`, mounted globally including on `/login`).
 */
export function useVenues(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: venueKeys.all,
    queryFn: fetchVenues,
    staleTime: 60 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

function findVenueInfo(
  venues: VenueInfo[] | undefined,
  tradeVenue: TradeVenue
): VenueInfo | undefined {
  return venues?.find((venue) => venue.tradeVenue === tradeVenue)
}

export function useVenueInfo(tradeVenue: TradeVenue | null | undefined): VenueInfo | undefined {
  const { data } = useVenues()
  if (tradeVenue === null || tradeVenue === undefined) return undefined
  return findVenueInfo(data?.venues, tradeVenue)
}

/**
 * Server-supplied API-key onboarding instructions (`VenueInfo.apiKeyGuide`) —
 * where to create the key, which permissions to grant, and any venue caveat.
 * Resolved from the same config the backend checks the key against, and
 * environment-correct (dev serves testnet hosts, and Deribit needs one scope
 * fewer there). See docs/frontend-venue-api-key-guide.md in oems_v2.
 *
 * `undefined` covers three cases the caller must not distinguish: GetVenues
 * hasn't answered yet, the backend predates the field, or the venue mints
 * credentials itself (Hyperliquid/Derive/Lighter) and has no key step at all.
 * Callers fall back rather than rendering an empty checklist.
 */
export function useVenueApiKeyGuide(
  tradeVenue: TradeVenue | null | undefined
): ApiKeyGuide | undefined {
  return useVenueInfo(tradeVenue)?.apiKeyGuide
}

// `enabled` defaults true (rather than false) while GetVenues hasn't answered
// yet — the venue registry is still loading — so callers gate a fetch/list by
// server truth once available without blocking on it. Once GetVenues HAS
// answered, though, a tradeVenue that's simply absent from the response (e.g.
// a venue removed outright from the backend's registry, not returned with
// `enabled: false`) defaults to NOT enabled — the server has spoken, and
// silence means "no live venue", not "assume yes".
export function isTradeVenueEnabled(
  venues: VenueInfo[] | undefined,
  tradeVenue: TradeVenue
): boolean {
  if (venues === undefined) return true
  return findVenueInfo(venues, tradeVenue)?.enabled ?? false
}

// Same as isTradeVenueEnabled, but for callers that only have an Exchange
// (not a TradeVenue) in hand — e.g. exchanges with no account-creation venue
// at all (IBKR/Databento) default to enabled since they're market-data-only
// and never go through this gate in practice.
export function isExchangeEnabled(venues: VenueInfo[] | undefined, exchange: Exchange): boolean {
  const tradeVenue = exchangeToTradeVenue(exchange)
  if (tradeVenue === undefined) return true
  return isTradeVenueEnabled(venues, tradeVenue)
}

/**
 * Per-exchange `enabled` (GetVenues), keyed by exchange (not positional) so
 * reordering `exchanges` can't silently pair the wrong flag with the wrong
 * exchange. For a fixed set of exchanges fetched via individual hook calls —
 * see `useEnabledExchangeList` for the array-filtering equivalent.
 */
export function useExchangesEnabled(exchanges: Exchange[]): Map<Exchange, boolean> {
  const { data } = useVenues()
  return useMemo(
    () =>
      new Map(exchanges.map((exchange) => [exchange, isExchangeEnabled(data?.venues, exchange)])),
    [exchanges, data?.venues]
  )
}

/**
 * Filters `exchanges` down to the ones GetVenues reports as `enabled`,
 * honouring a backend venue disable (e.g. Binance has no instrument catalog
 * in prod) instead of the frontend continuing to poll it forever. Safe for
 * `useQueries`-driven fetches (variable-length arrays don't trip the rules of
 * hooks) — use `useExchangesEnabled` instead for a fixed set of direct hook
 * calls.
 */
export function useEnabledExchangeList(exchanges: Exchange[]): Exchange[] {
  const enabled = useExchangesEnabled(exchanges)
  return useMemo(() => exchanges.filter((exchange) => enabled.get(exchange)), [exchanges, enabled])
}

/**
 * Whether GetVenues has answered at least once. `isExchangeEnabled` (and
 * therefore `useEnabledExchangeList`) deliberately defaults an exchange to
 * "enabled" while venues data hasn't loaded yet, so a catalog fetch for a
 * venue that turns out to be enabled isn't held up waiting on GetVenues. That
 * default is wrong to build a *fetch gate* out of directly, though: a caller
 * that fires a request as soon as `useEnabledExchangeList` returns something
 * (e.g. the watchlist-wizard master-instrument prefetch) would send one
 * doomed request per venue GetVenues is about to report `enabled: false` for
 * (e.g. Binance, which has no instrument catalog) on every mount before the
 * real answer lands. Callers that actually issue a network request per
 * exchange — not just ones filtering a list for display — should AND this in
 * so they wait for server truth instead of guessing.
 */
export function useVenuesLoaded(): boolean {
  const { data } = useVenues()
  return data !== undefined
}

/**
 * Disconnect a Hyperliquid agent-wallet account (owner-gated). On success the
 * accounts query is invalidated so the row reflects the Inactive status.
 */
export function useHyperliquidDisconnect(props?: {
  onSuccess?: (data: Success, accountId: string) => void
  onError?: (error: ErrorResponse, accountId: string) => void
}) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (accountId: string) => hyperliquidDisconnectAgent({ accountId }),
    onSuccess: (data, accountId) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all(user?.userId) })
      props?.onSuccess?.(data, accountId)
    },
    onError: props?.onError,
  })
}

/**
 * Disconnect a Derive session-key account (owner-gated). On success the
 * accounts query is invalidated so the row reflects the Inactive status. This
 * only clears OrderX's stored copy of the key — it does not deregister it at
 * Derive (see the disconnect UI copy in the wizard, Task 12, and the account
 * settings dialog, Task 11).
 */
export function useDeriveDisconnect(props?: {
  onSuccess?: (data: Success, accountId: string) => void
  onError?: (error: ErrorResponse, accountId: string) => void
}) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (accountId: string) => deriveDisconnect({ accountId }),
    onSuccess: (data, accountId) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all(user?.userId) })
      props?.onSuccess?.(data, accountId)
    },
    onError: props?.onError,
  })
}

/** Disconnect a Papertrade account (owner-gated); same shape as `useDeriveDisconnect`. */
export function usePapertradeDisconnect(props?: {
  onSuccess?: (data: Success, accountId: string) => void
  onError?: (error: ErrorResponse, accountId: string) => void
}) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (accountId: string) => papertradeDisconnect({ accountId }),
    onSuccess: (data, accountId) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all(user?.userId) })
      props?.onSuccess?.(data, accountId)
    },
    onError: props?.onError,
  })
}

/**
 * Unlink an account's Paradigm desk binding (owner-gated). Invalidating the accounts
 * query is what returns the settings row to "not connected" — `PublicAccount
 * .paradigmBinding` is the only place the binding is carried, so nothing else has to be
 * refetched. Sits here beside the Derive/Hyperliquid disconnects rather than with the
 * Paradigm wizard hook: those three share this exact shape, and the wizard's other RPCs
 * are plain promises, not mutations.
 */
export function useParadigmDisconnect(props?: {
  onSuccess?: (data: Success, accountId: string) => void
  onError?: (error: ErrorResponse, accountId: string) => void
}) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (accountId: string) => paradigmDisconnect({ accountId }),
    onSuccess: (data, accountId) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all(user?.userId) })
      props?.onSuccess?.(data, accountId)
    },
    onError: props?.onError,
  })
}
